import { createContext, useContext, useMemo } from 'react'
import { useLocation } from 'react-router-dom'

import { Experiments } from 'constants/experiments'
import useExperiment from 'hooks/Experiments/useExperiment'
import { useAppSelector } from 'hooks/reduxHooks'
import { useIsMobileDevice } from 'hooks/useIsMobileDevice'
import { matchLocaleAwarePath } from 'lib/routing/matchLocaleAwarePath'

const META_SEARCH_PAGE_PATHS = [
  '/:regionCode/metasearch/:propertyId',
]

/** Page-owned V2 flag. Tiles should read this via `useMetaSearchMobileV2Enabled`. */
export const MetaSearchMobileV2Context = createContext<boolean | undefined>(undefined)

/** Context read for tiles/leaves. Falls back to `false` outside the page provider. */
export function useMetaSearchMobileV2Enabled(): boolean {
  return useContext(MetaSearchMobileV2Context) ?? false
}

/**
 * Resolves Optimizely + eligibility. Activate only from the page owner;
 * use `useMetaSearchMobileV2Enabled` everywhere else under the provider.
 */
function useMetaSearchMobileV2(activate = false): boolean {
  const isMobile = useIsMobileDevice()
  const { pathname } = useLocation()
  const utmBp = useAppSelector(state => state.utm.bp)

  const isMetaSearchPage = useMemo(
    () => !!matchLocaleAwarePath(pathname, { path: META_SEARCH_PAGE_PATHS, exact: true }),
    [pathname],
  )
  const isEligible = utmBp === '3' && isMobile && isMetaSearchPage
  const { isAnyVariant } = useExperiment(
    Experiments.growthMetasearchMobileDesignV2,
    { canActivate: activate && isEligible },
  )

  return isEligible && isAnyVariant
}

export default useMetaSearchMobileV2
